import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { ESPLoader, Transport } from 'esptool-js';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { getProvisionBlob, setNodeMcuMac, type ProvisionBlob } from '@/api/vendo';

interface Props {
   /** device._id — used to fetch the per-device provision blob */
   deviceId: string;
   /** Called once flash + reset succeed. Wizard advances on this. */
   onSuccess?: () => void;
   /** Called when user clicks "Skip — flash later" in the manual fallback. */
   onSkip?: () => void;
   /** Registration only: show the "Flash the NodeMCU later" tick. Device
    *  Settings → Hardware re-flashes a vendo that is already set up, so there is
    *  nothing to "go on" to — it gets a plain Cancel instead (Ray 2026-10-20). */
   offerFlashLater?: boolean;
}

type Phase =
   | 'idle'
   | 'connected'
   | 'fetching-blob'
   | 'fetching-firmware'
   | 'flashing'
   | 'success'
   | 'error';

interface FlashState {
   phase: Phase;
   port: SerialPort | null;
   progressPct: number;
   errorMsg: string | null;
   chipName: string | null;
   firmwareVersion: string | null;
   /** Server-side notice that the WiFi name just written to the chip is not the
    *  one this router broadcasts. The chip cannot tell anyone afterwards — it
    *  simply never appears — so this is the last place it can be said. */
   wifiWarning: string | null;
}

const INITIAL_STATE: FlashState = {
   phase: 'idle',
   port: null,
   progressPct: 0,
   errorMsg: null,
   chipName: null,
   firmwareVersion: null,
   wifiWarning: null,
};

// USB vendor IDs for the USB-serial chips found on NodeMCU dev boards.
// CH340 is overwhelmingly the most common; CP2102 shows up on slightly
// higher-end boards; FTDI FT232 is less common but legitimate (some
// dev boards and older NodeMCU revisions ship with it).
const NODEMCU_FILTERS: SerialPortFilter[] = [
   { usbVendorId: 0x1a86 }, // QinHeng (CH340)
   { usbVendorId: 0x10c4 }, // Silicon Labs (CP2102/CP2104)
   { usbVendorId: 0x0403 }, // FTDI (FT232)
];

const hasWebSerial = (): boolean =>
   typeof navigator !== 'undefined' && 'serial' in navigator;

function base64ToUint8Array(b64: string): Uint8Array {
   const binary = atob(b64);
   const out = new Uint8Array(binary.length);
   for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
   return out;
}

const NodeMcuFlasher = ({ deviceId, onSuccess, onSkip, offerFlashLater = false }: Props) => {
   const [state, setState] = useState<FlashState>(INITIAL_STATE);
   const [flashLater, setFlashLater] = useState(false);
   // Hold the loader so we can attempt graceful disconnect on retry/unmount.
   const loaderRef = useRef<ESPLoader | null>(null);
   const transportRef = useRef<Transport | null>(null);

   // Release the port so a subsequent flash can reopen it. esptool-js's
   // ESPLoader.connect() opens the port internally; if we hit an error
   // mid-flash, the port stays open and any retry trips
   // "InvalidStateError: The port is already open." Always call this
   // before resetting state on failure or retry.
   const releaseTransport = async () => {
      const t = transportRef.current;
      transportRef.current = null;
      loaderRef.current = null;
      if (!t) return;
      try {
         await t.disconnect();
      } catch {
         // Already closed or never opened — nothing to do.
      }
   };

   useEffect(() => {
      return () => {
         void releaseTransport();
      };
   }, []);

   const detectPort = async () => {
      if (!hasWebSerial()) {
         toast.error('Open this in Chrome or Edge — Safari/Firefox don\'t support WebSerial yet.');
         return;
      }
      try {
         const port = await navigator.serial.requestPort({ filters: NODEMCU_FILTERS });
         setState({ ...INITIAL_STATE, phase: 'connected', port });
      } catch (err) {
         // User cancelled the picker (NotFoundError / AbortError) — silent.
         const e = err as DOMException;
         if (e.name !== 'NotFoundError' && e.name !== 'AbortError') {
            toast.error(`Could not connect: ${e.message || e.name}`);
         }
      }
   };

   const startFlash = async () => {
      if (state.phase !== 'connected' || !state.port) return;
      const port = state.port;

      // Defensive: close any transport still holding this port from a prior
      // attempt before opening it again. esptool-js's connect() does port.open()
      // internally, which throws "port is already open" on a held port.
      await releaseTransport();

      try {
         // 1. Fetch provision blob from server. This includes the URL of
         // the base firmware and the pre-computed identity sector ready to
         // write at 0x3FB000 — 84 bytes (wired) or 189 bytes when the device
         // is provisioned wireless (identity + WiFi block). We trust the
         // server-reported size/address rather than hardcoding either.
         setState((s) => ({ ...s, phase: 'fetching-blob', errorMsg: null }));
         const blob: ProvisionBlob = await getProvisionBlob(deviceId);
         if (blob.wifiWarning) {
            setState((s) => ({ ...s, wifiWarning: blob.wifiWarning ?? null }));
         }

         // 2. Fetch the base firmware .bin straight from its hosting URL.
         // Server has CORS configured for browser fetches across ports.
         console.log(
            `[flasher] flashing firmware v${blob.firmwareVersion} → device ${deviceId}`
         );
         setState((s) => ({ ...s, phase: 'fetching-firmware', firmwareVersion: blob.firmwareVersion }));
         const firmwareResp = await fetch(blob.baseFirmwareUrl);
         if (!firmwareResp.ok) {
            throw new Error(`Firmware fetch failed: HTTP ${firmwareResp.status}`);
         }
         const firmwareBytes = new Uint8Array(await firmwareResp.arrayBuffer());

         // 3. Decode the identity sector from base64 to raw bytes.
         const identityBytes = base64ToUint8Array(blob.identitySector.bytesBase64);
         if (identityBytes.length !== blob.identitySector.size) {
            throw new Error(
               `Identity sector size mismatch: got ${identityBytes.length}, expected ${blob.identitySector.size}`
            );
         }

         // 4. Bring up the esptool-js loader against this port.
         const transport = new Transport(port, true);
         transportRef.current = transport;
         const esploader = new ESPLoader({
            transport,
            baudrate: 115200,
            // No console output during flash — progress is surfaced in
            // the UI via reportProgress and the phase state. Operators
            // don't need esptool's per-packet stream and the page slows
            // noticeably with thousands of console.logs.
            terminal: {
               clean: () => {},
               writeLine: () => {},
               write: () => {},
            },
            debugLogging: false,
            enableTracing: false,
         } as any);
         loaderRef.current = esploader;

         const chipName = await esploader.main();
         setState((s) => ({ ...s, chipName }));
         // Sanity-check that the operator plugged in an ESP8266, not an ESP32.
         // The identity sector address (0x3FB000) and base.bin layout are
         // ESP8266-specific.
         if (!chipName.toLowerCase().includes('esp8266')) {
            throw new Error(
               `Unexpected chip "${chipName}" — this flow targets ESP8266 NodeMCUs only.`
            );
         }

         // Capture the NodeMCU's WiFi MAC and POST it to the server BEFORE
         // we move on to writeFlash. Doing it now (vs after the flash) means
         // even if the flash fails the operator's next attempt at Step 4
         // ("Apply Config") gets section 3 emitted in the router script.
         //
         // Best-effort: a MAC-POST failure here is not fatal — the operator
         // can still proceed; first heartbeat will fill the MAC the slow
         // way. We log + warn but don't throw.
         try {
            const rawMac = await (esploader.chip as any).readMac(esploader);
            // esptool-js returns "aa:bb:cc:dd:ee:ff" lowercased; normalize.
            const mac = String(rawMac).toUpperCase();
            if (/^([0-9A-F]{2}:){5}[0-9A-F]{2}$/.test(mac)) {
               await setNodeMcuMac(deviceId, mac);
            } else {
               console.warn('[flasher] readMac returned unexpected format:', rawMac);
            }
         } catch (macErr) {
            console.warn(
               '[flasher] MAC capture failed (will be filled by first heartbeat):',
               macErr instanceof Error ? macErr.message : macErr
            );
         }

         // 5. Write both files in one writeFlash call. esptool-js handles
         // erase + write per region. Compress speeds up the transfer
         // ~3x for the firmware payload.
         //
         // eraseAll: TRUE (full chip erase) is required, not just per-region.
         // We only write firmware @0x0000 + identity @0x3FB000, so the SDK
         // system-param sectors at 0x3FC000–0x3FF000 (RF calibration / phy-init)
         // are NOT touched by a per-region erase. On a previously-flashed chip
         // those hold STALE calibration, so the FIRST boot's radio is
         // mis-calibrated and WiFi won't connect — even though the SSID/creds in
         // the identity sector are correct. The first boot then re-calibrates +
         // saves fresh data, which is why a SECOND flash "fixes" it. A full erase
         // makes the SDK regenerate calibration from defaults on the first boot,
         // so the first flash connects every time (~20–30s slower erase).
         setState((s) => ({ ...s, phase: 'flashing', progressPct: 0 }));
         await esploader.writeFlash({
            fileArray: [
               { data: firmwareBytes, address: 0x0000 },
               { data: identityBytes, address: blob.identitySector.address },
            ],
            flashMode: 'dio',
            flashFreq: '40m',
            flashSize: '4MB',
            eraseAll: true,
            compress: true,
            reportProgress: (_fileIndex, written, total) => {
               const pct = total > 0 ? Math.round((written / total) * 100) : 0;
               setState((s) =>
                  s.phase === 'flashing' ? { ...s, progressPct: pct } : s
               );
            },
         });

         // 6. Hard reset so the firmware boots into our application
         // instead of staying in download mode.
         await esploader.after('hard_reset');

         // Release the serial port now that the flash is done. Without this the
         // port stays OPEN after a successful flash, so the NEXT flash in the
         // same session (e.g. flashing several sub-vendo NodeMCUs back-to-back)
         // can't open it → "Failed to open serial port"/"Failed to connect".
         // The error/retry/unmount paths already release; success did not.
         await releaseTransport();

         setState((s) => ({ ...s, phase: 'success', progressPct: 100 }));
         toast.success('NodeMCU flashed successfully', { autoClose: 2500 });
         setTimeout(() => onSuccess?.(), 1500);
      } catch (err) {
         // The server's own words when it refused (e.g. no NodeMCU firmware
         // published for this kind of vendo yet) — "Request failed with status
         // code 503" tells an operator nothing they can act on.
         const serverSaid = (err as { response?: { data?: { error?: unknown } } })
            ?.response?.data?.error;
         const msg =
            typeof serverSaid === 'string' && serverSaid
               ? serverSaid
               : err instanceof Error
                 ? err.message
                 : 'Flash failed';
         console.error('[flasher] failure:', err);
         await releaseTransport();
         setState((s) => ({
            ...s,
            phase: 'error',
            errorMsg: msg,
         }));
         toast.error(msg);
      }
   };

   const retry = async () => {
      // Release the port esptool-js may still hold from the failed
      // attempt; without this, port.open() in connect() throws
      // InvalidStateError. Keep the granted port reference — re-running
      // detect would force the user to re-pick from the OS picker.
      await releaseTransport();
      const port = state.port;
      setState({ ...INITIAL_STATE, phase: port ? 'connected' : 'idle', port });
   };

   /* ─── Render branches ─────────────────────────────────── */

   if (!hasWebSerial()) {
      return (
         <Wrapper>
            <BrowserWarning>
               <strong>Browser not supported.</strong> The in-browser flasher needs WebSerial,
               which only works in Chrome, Edge, or Opera (desktop or Android). Safari and
               Firefox can't run this. Open the dashboard in a supported browser, or use the
               manual flash flow with PlatformIO.
            </BrowserWarning>
            {onSkip && (
               <SkipLink type="button" onClick={onSkip}>
                  Skip — I'll flash manually
               </SkipLink>
            )}
         </Wrapper>
      );
   }

   return (
      <Wrapper>
         {state.phase === 'idle' && (
            <>
               {!flashLater && (
                  <>
                     <Hint>
                        Plug the NodeMCU into a USB port, then press
                        Detect.
                     </Hint>
                     <PrimaryBtn onClick={detectPort}>Detect NodeMCU</PrimaryBtn>
                  </>
               )}
               {/* The vendo is already registered at this point; the NodeMCU
                   can be flashed any time from Device Settings → Hardware.
                   Same boxed tick as the MikroTik wizard; ticking it hides
                   Detect and shows a plain Next (Ray 2026-10-15). */}
               {onSkip && !offerFlashLater && (
                  <SkipLink type="button" onClick={onSkip}>
                     Cancel
                  </SkipLink>
               )}
               {onSkip && offerFlashLater && (
                  <>
                     <FlashLaterRow>
                        <input
                           type="checkbox"
                           checked={flashLater}
                           onChange={(e) => setFlashLater(e.target.checked)}
                        />
                        <span>
                           <strong>Flash the NodeMCU later.</strong> Go on now; the
                           vendo is registered either way. Flash the NodeMCU from
                           Device Settings → Hardware when it is on hand.
                        </span>
                     </FlashLaterRow>
                     {flashLater && (
                        <BtnRow style={{ justifyContent: 'flex-end' }}>
                           <PrimaryBtn type="button" onClick={onSkip}>
                              Next
                           </PrimaryBtn>
                        </BtnRow>
                     )}
                  </>
               )}
               {flashLater ? null : (
               <>
               {/* The #1 stuck point for new operators: the board needs the
                   CH340 USB driver on Windows before the browser can list
                   its port. Boxed + amber so it reads as a real step, not a
                   footnote. Drivers served from our Downloads externals;
                   Linux omitted (kernel ships the driver). */}
               <DriverHelp>
                  <span>
                     ⚠ First time on this computer? Install the USB driver,
                     then unplug &amp; replug:
                  </span>
                  <a
                     href={`${import.meta.env.VITE_API_URL}/openwrt/external/nodemcu-driver-windows/download`}
                  >
                     Windows
                  </a>
                  <a
                     href={`${import.meta.env.VITE_API_URL}/openwrt/external/nodemcu-driver-mac/download`}
                  >
                     Mac
                  </a>
               </DriverHelp>
               </>
               )}
            </>
         )}

         {state.phase === 'connected' && (
            <>
               <SuccessLine>
                  <Dot $color={colors.SUCCESS} />
                  <span>USB port granted.</span>
               </SuccessLine>
               <PrimaryBtn onClick={startFlash}>Start flash</PrimaryBtn>
            </>
         )}

         {(state.phase === 'fetching-blob' || state.phase === 'fetching-firmware') && (
            <ProgressPanel>
               <Spinner />
               <ProgressLabel>
                  {state.phase === 'fetching-blob'
                     ? 'Preparing identity sector…'
                     : `Downloading firmware${
                          state.firmwareVersion ? ` v${state.firmwareVersion}` : ''
                       }…`}
               </ProgressLabel>
            </ProgressPanel>
         )}

         {state.phase === 'flashing' && (
            <ProgressPanel>
               <ProgressLabel>
                  Flashing {state.chipName || 'ESP8266'} — {state.progressPct}%
               </ProgressLabel>
               <ProgressBar>
                  <ProgressBarFill style={{ width: `${state.progressPct}%` }} />
               </ProgressBar>
               {state.firmwareVersion && (
                  <ProgressSub>
                     You are flashing <strong>v{state.firmwareVersion}</strong>
                  </ProgressSub>
               )}
               <ProgressSub>Don't unplug. Takes about a minute.</ProgressSub>
            </ProgressPanel>
         )}

         {state.phase === 'success' && (
            <>
               <SuccessPanel>
                  <Dot $color={colors.SUCCESS} />
                  <span>
                     <strong>Done.</strong> Identity sector written. Boot to confirm{' '}
                     <Mono>[identity] loaded from sector</Mono>.
                  </span>
               </SuccessPanel>
               {state.wifiWarning && (
                  <WarnPanel>
                     <Dot $color={colors.WARNING} />
                     <span>
                        <strong>Check the WiFi name.</strong> {state.wifiWarning}
                     </span>
                  </WarnPanel>
               )}
            </>
         )}

         {state.phase === 'error' && (
            <>
               <ErrorPanel>
                  <Dot $color={colors.DANGER} />
                  <span>
                     <strong>Flash failed.</strong> {state.errorMsg}
                  </span>
               </ErrorPanel>
               <BtnRow>
                  <PrimaryBtn onClick={retry}>Retry</PrimaryBtn>
                  {onSkip && (
                     <SkipLink type="button" onClick={onSkip}>
                        Skip — flash manually
                     </SkipLink>
                  )}
               </BtnRow>
            </>
         )}
      </Wrapper>
   );
};

const WarnPanel = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 8px;
   margin-top: 8px;
   padding: 10px 12px;
   border: 1px solid ${colors.WARNING};
   border-radius: 8px;
   background: ${colors.WARNING}1a;
   font-size: 12px;
   line-height: 1.5;
`;

const Wrapper = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
   padding: 12px 14px;
   width: 100%;
   box-sizing: border-box;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
`;

const Hint = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   line-height: 1.5;
`;

const DriverHelp = styled.div`
   display: flex;
   align-items: center;
   flex-wrap: wrap;
   gap: 6px;
   padding: 7px 10px;
   border: 1px solid rgba(240, 195, 109, 0.5);
   background: rgba(240, 195, 109, 0.12);
   border-radius: 8px;
   font-size: 11.5px;
   color: #8a6d1a;
   line-height: 1.4;
   a {
      color: ${colors.PRIMARY};
      font-weight: 800;
      text-decoration: underline;
      padding: 1px 6px;
   }
   /* Readable in dark mode too (Ray 2026-10-18: amber-on-grey was unreadable). */
   [data-theme='dark'] & {
      color: #f5cf7a;
      a { color: ${colors.PRIMARY70}; }
   }
`;

const PrimaryBtn = styled.button`
   align-self: flex-start;
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   padding: 8px 16px;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 6px;
   cursor: pointer;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   &:hover { opacity: 0.9; }
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

const SuccessLine = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 12px;
   color: ${colors.MAIN_DARK};
`;

const Dot = styled.span<{ $color: string }>`
   display: inline-block;
   width: 10px;
   height: 10px;
   border-radius: 50%;
   background: ${({ $color }) => $color};
   flex-shrink: 0;
`;

const ProgressPanel = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const ProgressLabel = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const ProgressSub = styled.span`
   font-size: 10px;
   color: ${colors.GRAY5};
`;

const ProgressBar = styled.div`
   height: 6px;
   background: ${colors.GRAY12};
   border-radius: 3px;
   overflow: hidden;
`;

const ProgressBarFill = styled.div`
   height: 100%;
   background: ${colors.PRIMARY};
   transition: width 0.2s;
`;

const spin = keyframes`
   to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
   width: 14px;
   height: 14px;
   border: 2px solid ${colors.GRAY12};
   border-top-color: ${colors.PRIMARY};
   border-radius: 50%;
   animation: ${spin} 0.8s linear infinite;
`;

const SuccessPanel = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 10px 12px;
   font-size: 12px;
   color: ${colors.MAIN_DARK};
   background: ${colors.SUCCESS}10;
   border: 1px solid ${colors.SUCCESS}30;
   border-radius: 6px;
`;

const ErrorPanel = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 10px 12px;
   font-size: 12px;
   color: ${colors.MAIN_DARK};
   background: ${colors.DANGER}10;
   border: 1px solid ${colors.DANGER}30;
   border-radius: 6px;
`;

const BtnRow = styled.div`
   display: flex;
   gap: 10px;
   align-items: center;
`;

const FlashLaterRow = styled.label`
   display: flex;
   align-items: flex-start;
   gap: 10px;
   margin: 12px 0 4px;
   padding: 12px 14px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   background: ${colors.GRAY14};
   font-size: 13px;
   line-height: 1.45;
   color: ${colors.GRAY3};
   cursor: pointer;
   input {
      margin-top: 3px;
      width: 16px;
      height: 16px;
      accent-color: ${colors.PRIMARY};
      flex-shrink: 0;
   }
   strong {
      color: ${colors.MAIN_DARK};
   }
`;
const SkipLink = styled.button`
   font-family: inherit;
   font-size: 11px;
   font-weight: 700;
   padding: 6px 10px;
   background: transparent;
   color: ${colors.GRAY5};
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   cursor: pointer;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   &:hover {
      color: ${colors.MAIN_DARK};
      border-color: ${colors.GRAY10};
   }
`;

const BrowserWarning = styled.p`
   font-size: 12px;
   color: ${colors.MAIN_DARK};
   line-height: 1.5;
   padding: 10px 12px;
   background: ${colors.WARNING}10;
   border: 1px solid ${colors.WARNING}40;
   border-radius: 6px;
`;

const Mono = styled.code`
   font-family: 'SF Mono', ui-monospace, Menlo, monospace;
   font-size: 11px;
   padding: 1px 5px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 4px;
   color: ${colors.MAIN_DARK};
`;

export default NodeMcuFlasher;
